<template>
  <div class="skeleton" :class="`radius-${radius}`" :style="{ width, height }" aria-hidden="true" />
</template>

<script setup lang="ts">
/**
 * Holds the space of something still loading, at the size it will take, so the page around it does
 * not jump when it arrives. Sized by the caller: the skeleton knows nothing about what it stands for.
 */
withDefaults(
  defineProps<{
    width?: string;
    height?: string;
    /** Matches the corner of what it stands for: `lg` for a card, `sm` for a control, `full` for an avatar */
    radius?: 'sm' | 'md' | 'lg' | 'full';
  }>(),
  { width: '100%', height: '1rem', radius: 'sm' },
);
</script>

<style scoped lang="scss">
.skeleton {
  flex-shrink: 0;
  background-color: var(--t-color-neutral-200);
  animation: skeleton-breathe 1.6s ease-in-out infinite;

  &.radius-sm {
    border-radius: var(--t-border-radius-sm);
  }

  &.radius-md {
    border-radius: var(--t-border-radius-md);
  }

  &.radius-lg {
    border-radius: var(--t-border-radius-lg);
  }

  &.radius-full {
    border-radius: var(--t-border-radius-full-rounded);
  }
}

// A slow fade says "still loading" without the movement a shimmer adds to a page that is otherwise still
@keyframes skeleton-breathe {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.55;
  }
}

@media (prefers-reduced-motion: reduce) {
  .skeleton {
    animation: none;
  }
}
</style>
